import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { checkSession } from './store/slices/authSlice';
import { resetPracticeState } from './store/slices/practiceSlice';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Layout from './components/Layout/Layout/Layout';
import SessionTimeout from './components/Common/SessionTimeout/SessionTimeout';
import { subscribeToSessionExpired } from './services/sessionEvents.js';
import LoginPage from './pages/Auth/LoginPage';
import OTPStep from './pages/Auth/OTPStep';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';

// Pages
import UserManagement from './pages/UserManagement/UserManagement';
import PracticeManagement from './pages/PracticeManagement/PracticeManagement';
import PracticeDashboard from './pages/PracticeManagement/PracticeDashboard/PracticeDashboard';
import AddPractice from './pages/PracticeManagement/PracticesList/AddPractice/AddPractice';
import AddProvider from './pages/PracticeManagement/ProviderManagement/AddProvider/AddProvider';
import AddCaregiver from './pages/PracticeManagement/CaregiverManagement/AddCaregiver/AddCaregiver';
import AddPatient from './pages/PracticeManagement/PatientManagement/AddPatient/AddPatient';
import PatientDetails from './pages/PracticeManagement/PatientManagement/PatientDetails';
import PatientEditView from './pages/PracticeManagement/PatientManagement/PatientEditView/PatientEditView';
import FollowUpList from './pages/PracticeManagement/PatientManagement/FollowUpList/FollowUpList';
import ImportPatients from './pages/PracticeManagement/ImportPatients/ImportPatients';
import AddAdmin from './pages/PracticeManagement/AdminManagement/AddAdmin/AddAdmin';
import AdminManagement from './pages/PracticeManagement/AdminManagement/AdminManagement';
import AdminDetails from './pages/PracticeManagement/AdminManagement/AdminDetails/AdminDetails';
import AddSystemCaregiver from './pages/SystemCaregiverDashboard/AddSystemCaregiver/AddSystemCaregiver';
import MeasurementData from './pages/MeasurementData/MeasurementData';
import QuestionnaireSystem from './pages/QuestionnaireSystem/QuestionnaireSystem';
import ChatSystem from './pages/ChatSystem/ChatSystem';
import BillingDashboard from './pages/BillingReports/BillingDashboard';
import ManageBillingReport from './pages/BillingReports/ManageBillingReport';
import BillingCycleReport from './pages/BillingReports/BillingCycleReport';
import Settings from './pages/Settings/Settings';
import InventoryList from './pages/Inventory/InventoryList/InventoryList';
import InventoryDetails from './pages/Inventory/InventoryDetails/InventoryDetails';
import EmailLogs from './pages/EmailLogs/EmailLogs';
import EventLogsPage from './pages/EventLogs/EventLogsPage';
import Permissions from './pages/Permissions/Permissions';
import VisitType from './pages/CalendarSettings/VisitType/VisitType';
import WorkingHours from './pages/CalendarSettings/WorkingHours/WorkingHours';
import Calendar from './pages/CalendarSettings/Calendar/Calendar';
import FollowUpQuery from './pages/CareManagement/FollowUpQuery';
import CCMDashboard from './pages/CCM/CCMDashboard';
import CCMConfiguration from './pages/CCM/CCMConfiguration';
import EnrollmentQueue from './pages/CCM/EnrollmentQueue';
import EnrollPatient from './pages/CCM/EnrollPatient';
import EnrolledPatients from './pages/CCM/EnrolledPatients';
import CarePlans from './pages/CCM/CarePlans';
import Timer from './pages/CCM/Timer';
import TimeTracking from './pages/CCM/TimeTracking';
import Billing from './pages/CCM/Billing';
import PHMHub from './pages/CCM/PHMHub';

// Role-based Dashboard Pages
import ProviderDashboard from './pages/ProviderDashboard/ProviderDashboard';
import CaregiverDashboard from './pages/CaregiverDashboard/CaregiverDashboard';
import SystemCaregiverDashboard from './pages/SystemCaregiverDashboard/SystemCaregiverDashboard';
import SystemCaregiverDetails from './pages/SystemCaregiverDashboard/SystemCaregiverDetails/SystemCaregiverDetails';
import PracticeAdminDashboard from './pages/PracticeAdminDashboard/PracticeAdminDashboard';
import PatientDashboard from './pages/PatientDashboard/PatientDashboard';
import PatientSettings from './pages/PatientDashboard/PatientSettings/PatientSettings';
import UserLogs from './pages/AdminManagement/UserLogs/UserLogs';

// Protected Route Component
import ProtectedRoute from './components/Auth/ProtectedRoute/ProtectedRoute';
import NonPatientRoute from './components/Auth/NonPatientRoute/NonPatientRoute';
import RoleBasedRedirect from './components/Common/RoleBasedRedirect/RoleBasedRedirect';
import PatientManagementRoutes from './pages/PatientManagement';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, isBootstrapped, user } = useSelector((state) => state.auth);

  const getProtectedRedirectTarget = () => {
    const roleId = Number(user?.role_id);
    // Caregiver users must always land on their own dashboard.
    if (roleId === 5) return '/caregiver-dashboard';
    // System Caregiver disabled
    // if (roleId === 7) return '/system-caregiver-dashboard';

    if (typeof window === 'undefined') {
      return '/dashboard';
    }
    const storedPath = sessionStorage.getItem('lastVisitedPath');
    if (!storedPath || storedPath === '/login' || storedPath === '/') {
      return '/dashboard';
    }
    return storedPath;
  };

  const protectedRedirectTarget = getProtectedRedirectTarget();

  const hasInitialized = React.useRef(false);

  useEffect(() => {
    // Check session on app load - ensure it only runs once
    if (!hasInitialized.current) {
      dispatch(checkSession());
      hasInitialized.current = true;
    }
  }, [dispatch, isAuthenticated]);

  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Catch-all for session expiration
    const unsubscribe = subscribeToSessionExpired(() => {
      handleSmoothRedirect();
    });

    return () => unsubscribe();
  }, []);

  const handleSmoothRedirect = () => {
    if (isRedirecting) return;
    
    setIsRedirecting(true);
    
    // Explicitly clear auth + practice state to stop stale dashboard loops
    dispatch(resetPracticeState());
    dispatch({ type: 'auth/clearAuth' }); 
    
    navigate('/login', { replace: true });
    
    // Short delay to let navigation settle before allowing next redirect
    setTimeout(() => {
      setIsRedirecting(false);
    }, 1000);
  };



  return (
    <BreadcrumbProvider>
      <div className="App">
        {/* Peaceful Redirect Overlay */}

        <SessionTimeout />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{
            top: '90px',
            right: '20px',
            zIndex: 100000
          }}
          toastStyle={{
            fontSize: '14px',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 100000
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />
          <Route
            path="/verify-otp"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <OTPStep />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleBasedRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdminManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/:adminId"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdminDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <PracticeManagement />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/add"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <AddPractice />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <PracticeDashboard />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-provider"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <AddProvider />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-caregiver"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <AddCaregiver />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          {/* Add patient OR edit-in-place from list (?editId= patients.id) — single long form (AddPatient.jsx) */}
          <Route
            path="/practices/:practiceId/add-patient"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <AddPatient />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <PatientDetails />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          {/* Tabbed patient workspace (profile / schedule / meters / docs) — PatientEditView.jsx; not the list ?editId= form */}
          <Route
            path="/practices/:practiceId/patients/:patientId/edit"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <PatientEditView />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId/follow-ups"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <FollowUpList />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId/measurements/*"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <MeasurementData />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/import-patients"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <ImportPatients />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-admin"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <AddAdmin />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          {/* System Caregiver routes disabled
          <Route
            path="/system-caregiver-dashboard/add-system-caregiver"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddSystemCaregiver />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-caregiver-dashboard/:caregiverId"
            element={
              <ProtectedRoute>
                <Layout>
                  <SystemCaregiverDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          */}

          {/* Role-based Dashboard Routes */}
          <Route
            path="/provider-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProviderDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* System Caregiver Dashboard route disabled
          <Route
            path="/system-caregiver-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SystemCaregiverDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          */}
          <Route
            path="/practice-admin-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <PracticeAdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CaregiverDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientSettings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientManagementRoutes />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/measurements"
            element={
              <ProtectedRoute>
                <Layout>
                  <MeasurementData />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/questionnaires"
            element={
              <ProtectedRoute>
                <Layout>
                  <QuestionnaireSystem />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChatSystem />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/manage/:practiceId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageBillingReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/cycle/:practiceId"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingCycleReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/:productId"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-logs"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmailLogs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/event-logs/:moduleId/:targetUserId"
            element={
              <ProtectedRoute>
                <Layout>
                  <EventLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/events-log"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserLogs />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/permission/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Permissions />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/visit-type"
            element={
              <ProtectedRoute>
                <Layout>
                  <VisitType />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/working-hours"
            element={
              <ProtectedRoute>
                <Layout>
                  <WorkingHours />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/calendar"
            element={
              <ProtectedRoute>
                <Layout>
                  <Calendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care/follow-up"
            element={
              <ProtectedRoute>
                <Layout>
                  <FollowUpQuery />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* CCM Routes */}
          <Route
            path="/ccm/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CCMDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/configuration"
            element={
              <ProtectedRoute>
                <Layout>
                  <CCMConfiguration />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enrollment/queue"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrollmentQueue />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enroll/:patientId"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrollPatient />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enrolled"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrolledPatients />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/care-plans/:enrollmentId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CarePlans />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/phm-hub/:enrollmentId"
            element={
              <ProtectedRoute>
                <Layout>
                  <PHMHub />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/timer"
            element={
              <ProtectedRoute>
                <Layout>
                  <Timer />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/time-tracking"
            element={
              <ProtectedRoute>
                <Layout>
                  <TimeTracking />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/billing"
            element={
              <ProtectedRoute>
                <Layout>
                  <Billing />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Root redirect */}
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? protectedRedirectTarget : "/login"} replace />}
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? protectedRedirectTarget : "/login"} replace />}
          />
        </Routes>
      </div>
    </BreadcrumbProvider>
  );
}

export default App;
